import React, { useState } from 'react';
import { 
  XMarkIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const EnquiryPurposeModal = ({ isOpen, onClose, leadData }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  if (!isOpen || !leadData) return null;

  const enquiryCategories = {
    "Website Development": {
      icon: "🌐",
      color: "bg-blue-100 text-blue-800 border-blue-200",
      description: "Complete website solutions for businesses",
      subcategories: [
        { name: "Business Website", price: "₹25,000 - ₹75,000", timeline: "2-4 weeks", features: ["Responsive Design", "SEO Optimized", "Contact Forms", "Admin Panel"] },
        { name: "E-commerce Website", price: "₹50,000 - ₹1,50,000", timeline: "4-8 weeks", features: ["Product Catalog", "Payment Gateway", "Inventory Management", "Order Tracking"] },
        { name: "Portfolio Website", price: "₹15,000 - ₹35,000", timeline: "1-2 weeks", features: ["Gallery", "Blog", "Contact Form", "Social Integration"] },
        { name: "Corporate Website", price: "₹75,000 - ₹2,00,000", timeline: "6-12 weeks", features: ["Multi-page", "CMS", "User Management", "Analytics"] }
      ]
    },
    "Mobile App Development": {
      icon: "📱",
      color: "bg-green-100 text-green-800 border-green-200",
      description: "Native and cross-platform mobile applications",
      subcategories: [
        { name: "Android App", price: "₹75,000 - ₹2,50,000", timeline: "8-16 weeks", features: ["Native Android", "Play Store", "Push Notifications", "Offline Support"] },
        { name: "iOS App", price: "₹1,00,000 - ₹3,00,000", timeline: "8-16 weeks", features: ["Native iOS", "App Store", "iCloud Integration", "Apple Pay"] },
        { name: "Cross-Platform App", price: "₹1,25,000 - ₹4,00,000", timeline: "10-20 weeks", features: ["React Native/Flutter", "Both Platforms", "Shared Codebase", "Cost Effective"] },
        { name: "Progressive Web App", price: "₹50,000 - ₹1,50,000", timeline: "6-12 weeks", features: ["Web-based", "App-like Experience", "Offline Capable", "Cross-platform"] }
      ]
    },
    "Digital Marketing": {
      icon: "📈",
      color: "bg-purple-100 text-purple-800 border-purple-200",
      description: "Complete digital marketing solutions",
      subcategories: [
        { name: "SEO Services", price: "₹15,000 - ₹50,000/month", timeline: "3-6 months", features: ["Keyword Research", "On-page SEO", "Link Building", "Analytics"] },
        { name: "Social Media Marketing", price: "₹20,000 - ₹75,000/month", timeline: "Ongoing", features: ["Content Creation", "Ad Campaigns", "Community Management", "Analytics"] },
        { name: "Google Ads", price: "₹25,000 - ₹1,00,000/month", timeline: "Ongoing", features: ["Campaign Setup", "Keyword Bidding", "Ad Copy", "Performance Tracking"] },
        { name: "Content Marketing", price: "₹30,000 - ₹1,00,000/month", timeline: "Ongoing", features: ["Blog Writing", "Video Content", "Infographics", "Email Marketing"] }
      ]
    },
    "Software Development": {
      icon: "💻",
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      description: "Custom software solutions for businesses",
      subcategories: [
        { name: "CRM Software", price: "₹1,50,000 - ₹5,00,000", timeline: "12-24 weeks", features: ["Lead Management", "Sales Pipeline", "Customer Database", "Reporting"] },
        { name: "ERP System", price: "₹3,00,000 - ₹15,00,000", timeline: "20-40 weeks", features: ["Inventory", "Accounting", "HR Management", "Multi-module"] },
        { name: "Custom Application", price: "₹1,00,000 - ₹10,00,000", timeline: "8-32 weeks", features: ["Tailored Solution", "Database Integration", "User Management", "API Integration"] },
        { name: "SaaS Platform", price: "₹5,00,000 - ₹25,00,000", timeline: "24-52 weeks", features: ["Multi-tenant", "Subscription Model", "Scalable", "Cloud-based"] }
      ]
    },
    "UI/UX Design": {
      icon: "🎨",
      color: "bg-pink-100 text-pink-800 border-pink-200",
      description: "User interface and experience design",
      subcategories: [
        { name: "Website UI/UX", price: "₹25,000 - ₹1,00,000", timeline: "2-6 weeks", features: ["Wireframes", "Mockups", "Prototypes", "User Testing"] },
        { name: "Mobile App UI/UX", price: "₹35,000 - ₹1,50,000", timeline: "3-8 weeks", features: ["App Flow", "Screen Design", "Interactive Prototypes", "Design System"] },
        { name: "Brand Identity", price: "₹20,000 - ₹75,000", timeline: "2-4 weeks", features: ["Logo Design", "Brand Guidelines", "Color Palette", "Typography"] },
        { name: "Dashboard Design", price: "₹30,000 - ₹1,25,000", timeline: "3-6 weeks", features: ["Data Visualization", "Admin Interface", "Charts & Graphs", "Responsive Design"] }
      ]
    },
    "E-commerce Solution": {
      icon: "🛒",
      color: "bg-orange-100 text-orange-800 border-orange-200",
      description: "Complete online store solutions",
      subcategories: [
        { name: "Shopify Store", price: "₹35,000 - ₹1,00,000", timeline: "2-4 weeks", features: ["Theme Customization", "Product Setup", "Payment Gateway", "Shipping Integration"] },
        { name: "WooCommerce Store", price: "₹40,000 - ₹1,25,000", timeline: "3-6 weeks", features: ["WordPress Integration", "Custom Plugins", "Payment Options", "Inventory Management"] },
        { name: "Custom E-commerce", price: "₹1,00,000 - ₹5,00,000", timeline: "8-16 weeks", features: ["Custom Development", "Advanced Features", "Scalable Architecture", "Third-party Integration"] },
        { name: "Marketplace Platform", price: "₹2,50,000 - ₹10,00,000", timeline: "16-32 weeks", features: ["Multi-vendor", "Commission System", "Advanced Search", "Analytics Dashboard"] }
      ]
    }
  };

  const currentCategory = enquiryCategories[leadData.title] || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                {currentCategory?.icon || "🎯"} {leadData.title || "Enquiry Details"}
              </h2>
              <p className="text-blue-100 mt-1">
                {currentCategory?.description || "Detailed breakdown of this enquiry"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Lead Basic Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Lead Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Name:</span>
                <p className="font-medium">{leadData.name}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Email:</span>
                <p className="font-medium">{leadData.email}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Phone:</span>
                <p className="font-medium">{leadData.phone || "Not provided"}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Company:</span>
                <p className="font-medium">{leadData.company || "Not provided"}</p>
              </div>
            </div>
          </div>

          {/* Service Categories */}
          {currentCategory ? (
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Available Options for {leadData.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentCategory.subcategories.map((subcategory, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{subcategory.name}</h4>
                      <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                        {subcategory.price}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <ClockIcon className="h-4 w-4 mr-2" />
                        Timeline: {subcategory.timeline}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Key Features:</p>
                      <div className="flex flex-wrap gap-1">
                        {subcategory.features.map((feature, idx) => (
                          <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                All Available Services
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(enquiryCategories).map(([category, details]) => (
                  <div 
                    key={category}
                    className={`border-2 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${details.color}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl">{details.icon}</span>
                      <ChevronRightIcon className="h-5 w-5" />
                    </div>
                    <h4 className="font-semibold mb-1">{category}</h4>
                    <p className="text-sm opacity-80">{details.description}</p>
                    <p className="text-xs mt-2 font-medium">
                      {details.subcategories.length} options available
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="mt-8 bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              Next Steps:
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Schedule a detailed consultation call</li>
              <li>• Discuss specific requirements and customizations</li>
              <li>• Provide detailed project proposal and timeline</li>
              <li>• Share portfolio examples and case studies</li>
              <li>• Finalize project scope and pricing</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
          <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Schedule Call
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnquiryPurposeModal;
